/**
 * CTRL_06 Dashboard - Model Performance Monitoring
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Card, Row, Col, Statistic, Table, Tag, Spin, Empty, Alert, Progress } from 'antd';
import {
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  LineChartOutlined,
  ThunderboltOutlined,
  AlertOutlined
} from '@ant-design/icons';
import { getControlResults, getControlEvents } from '../api/trustgridApi';
import { showError } from '../../../utils/notifications';

function CTRL_06_Dashboard({ projectId }) {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [events, setEvents] = useState([]);
  const controlCode = 'CTRL_06';

  useEffect(() => {
    if (projectId) {
      fetchData();
    }
  }, [projectId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [resultsRes, eventsRes] = await Promise.all([
        getControlResults(projectId),
        getControlEvents(projectId, controlCode).catch(() => ({ data: { events: [] } }))
      ]);

      const controlResults = (resultsRes.data.results || []).find(r => r.control_code === controlCode);
      setResults(controlResults);
      setEvents(eventsRes.data.events || []);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate latest metrics
  const latestMetrics = useMemo(() => {
    if (events.length === 0) return null;

    const sortedEvents = [...events].sort((a, b) => new Date(b.ts) - new Date(a.ts));
    const latest = sortedEvents[0];
    const metadata = latest?.event_metadata || {};

    return {
      accuracy: metadata.accuracy || 0,
      precision: metadata.precision || 0,
      recall: metadata.recall || 0,
      f1_score: metadata.f1_score || 0,
      latency_ms: metadata.latency_ms || 0,
      throughput: metadata.throughput || 0,
      error_rate: metadata.error_rate || 0,
      baseline_metrics: metadata.baseline_metrics || {}
    };
  }, [events]);

  // Calculate performance degradation
  const degradationAlerts = useMemo(() => {
    if (!latestMetrics || !latestMetrics.baseline_metrics) return [];

    const alerts = [];
    const baseline = latestMetrics.baseline_metrics;
    const threshold = 0.05; // 5% degradation threshold

    // Check accuracy degradation
    if (baseline.accuracy && latestMetrics.accuracy < baseline.accuracy * (1 - threshold)) {
      alerts.push({
        metric: 'Accuracy',
        current: (latestMetrics.accuracy * 100).toFixed(2) + '%',
        baseline: (baseline.accuracy * 100).toFixed(2) + '%',
        severity: 'high'
      });
    }

    // Check precision degradation
    if (baseline.precision && latestMetrics.precision < baseline.precision * (1 - threshold)) {
      alerts.push({
        metric: 'Precision',
        current: (latestMetrics.precision * 100).toFixed(2) + '%',
        baseline: (baseline.precision * 100).toFixed(2) + '%',
        severity: 'medium'
      });
    }

    // Check latency increase
    if (baseline.latency_ms && latestMetrics.latency_ms > baseline.latency_ms * (1 + threshold * 2)) {
      alerts.push({
        metric: 'Latency',
        current: latestMetrics.latency_ms.toFixed(2) + 'ms',
        baseline: baseline.latency_ms.toFixed(2) + 'ms',
        severity: 'high'
      });
    }

    // Check error rate increase
    if (baseline.error_rate && latestMetrics.error_rate > baseline.error_rate * (1 + threshold * 2)) {
      alerts.push({
        metric: 'Error Rate',
        current: (latestMetrics.error_rate * 100).toFixed(2) + '%',
        baseline: (baseline.error_rate * 100).toFixed(2) + '%',
        severity: 'high'
      });
    }

    return alerts;
  }, [latestMetrics]);

  // Detect drift
  const driftDetection = useMemo(() => {
    const recentEvents = events.slice(0, 20);
    if (recentEvents.length < 10) return { detected: false, details: [] };

    // Calculate Z-scores for accuracy
    const accuracies = recentEvents
      .map(e => e.event_metadata?.accuracy)
      .filter(a => a !== undefined && a !== null);

    if (accuracies.length < 5) return { detected: false, details: [] };

    const mean = accuracies.reduce((sum, val) => sum + val, 0) / accuracies.length;
    const variance = accuracies.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / accuracies.length;
    const stdDev = Math.sqrt(variance);

    const zScores = accuracies.map(val => Math.abs((val - mean) / (stdDev || 1)));
    const driftDetected = zScores.some(z => z > 3); // 3 standard deviations

    return {
      detected: driftDetected,
      mean: (mean * 100).toFixed(2) + '%',
      stdDev: (stdDev * 100).toFixed(4) + '%',
      maxZScore: Math.max(...zScores).toFixed(2)
    };
  }, [events]);

  // Table columns
  const columns = [
    {
      title: 'Timestamp',
      dataIndex: 'ts',
      key: 'ts',
      render: (ts) => new Date(ts).toLocaleString(),
      width: 180
    },
    {
      title: 'Model',
      dataIndex: 'model',
      key: 'model',
      ellipsis: true
    },
    {
      title: 'Accuracy',
      key: 'accuracy',
      render: (_, record) => {
        const acc = record.event_metadata?.accuracy;
        return acc ? `${(acc * 100).toFixed(2)}%` : '-';
      }
    },
    {
      title: 'Latency',
      key: 'latency',
      render: (_, record) => {
        const lat = record.event_metadata?.latency_ms;
        return lat ? `${lat.toFixed(2)}ms` : '-';
      }
    },
    {
      title: 'Error Rate',
      key: 'error_rate',
      render: (_, record) => {
        const err = record.event_metadata?.error_rate;
        return err ? `${(err * 100).toFixed(2)}%` : '-';
      }
    },
    {
      title: 'Drift',
      key: 'drift',
      align: 'center',
      render: (_, record) => {
        const driftDetected = record.event_metadata?.drift_detected;
        if (driftDetected === true) {
          return <Tag color="red" icon={<AlertOutlined />}>Detected</Tag>;
        }
        return <Tag color="green">Normal</Tag>;
      }
    }
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!projectId) {
    return (
      <Empty
        description="Please select a project to view dashboard"
        style={{ padding: '40px' }}
      />
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <h2>CTRL_06: Model Performance Monitoring Dashboard</h2>

      {/* Degradation Alerts */}
      {degradationAlerts.length > 0 && (
        <Alert
          message={`${degradationAlerts.length} Performance Degradation Alert(s)`}
          description={
            <div>
              {degradationAlerts.map((alert, idx) => (
                <div key={idx} style={{ marginBottom: '8px' }}>
                  <strong>{alert.metric}:</strong> {alert.current} (baseline: {alert.baseline})
                </div>
              ))}
            </div>
          }
          type="warning"
          icon={<WarningOutlined />}
          showIcon
          closable
          style={{ marginBottom: '24px' }}
        />
      )}

      {/* Drift Detection Alert */}
      {driftDetection.detected && (
        <Alert
          message="Statistical Drift Detected"
          description={
            <div>
              <div>Mean Accuracy: {driftDetection.mean}</div>
              <div>Std Dev: {driftDetection.stdDev}</div>
              <div>Max Z-Score: {driftDetection.maxZScore} (threshold: 3.0)</div>
            </div>
          }
          type="error"
          icon={<AlertOutlined />}
          showIcon
          closable
          style={{ marginBottom: '24px' }}
        />
      )}

      {/* Performance Metrics Summary */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Accuracy"
              value={latestMetrics ? (latestMetrics.accuracy * 100).toFixed(2) : 0}
              suffix="%"
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
            {latestMetrics?.baseline_metrics?.accuracy && (
              <div style={{ marginTop: '8px', fontSize: '13px', color: '#666' }}>
                Baseline: {(latestMetrics.baseline_metrics.accuracy * 100).toFixed(2)}%
              </div>
            )}
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Precision"
              value={latestMetrics ? (latestMetrics.precision * 100).toFixed(2) : 0}
              suffix="%"
              prefix={<LineChartOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Recall"
              value={latestMetrics ? (latestMetrics.recall * 100).toFixed(2) : 0}
              suffix="%"
              prefix={<LineChartOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="F1 Score"
              value={latestMetrics ? (latestMetrics.f1_score * 100).toFixed(2) : 0}
              suffix="%"
              prefix={<LineChartOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Operational Metrics */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Latency"
              value={latestMetrics ? latestMetrics.latency_ms.toFixed(2) : 0}
              suffix="ms"
              prefix={<ThunderboltOutlined />}
              valueStyle={{
                color: latestMetrics?.latency_ms < 100 ? '#52c41a' : latestMetrics?.latency_ms < 500 ? '#faad14' : '#f5222d'
              }}
            />
            <Progress
              percent={Math.min((latestMetrics?.latency_ms || 0) / 10, 100)}
              strokeColor={latestMetrics?.latency_ms < 100 ? '#52c41a' : latestMetrics?.latency_ms < 500 ? '#faad14' : '#f5222d'}
              size="small"
              style={{ marginTop: '8px' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Throughput"
              value={latestMetrics ? latestMetrics.throughput.toFixed(0) : 0}
              suffix="req/s"
              prefix={<ThunderboltOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Error Rate"
              value={latestMetrics ? (latestMetrics.error_rate * 100).toFixed(2) : 0}
              suffix="%"
              prefix={latestMetrics?.error_rate > 0.05 ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
              valueStyle={{
                color: latestMetrics?.error_rate < 0.01 ? '#52c41a' : latestMetrics?.error_rate < 0.05 ? '#faad14' : '#f5222d'
              }}
            />
          </Card>
        </Col>
      </Row>

      {/* Recent Performance Table */}
      <Card title="Recent Performance Metrics">
        <Table
          dataSource={events.slice(0, 20)}
          columns={columns}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} events`
          }}
          size="small"
        />
      </Card>
    </div>
  );
}

export default CTRL_06_Dashboard;
